import { Dispatch, ReactNode, SetStateAction, createContext, useContext, useEffect, useRef, useState } from "react";
import { IApiFeatureFlagsResponse, TApiFeatureFlagsResponse } from "@propely/propely-typings";
import { propelyApi } from "@src/libs/firebase/api";
import { getAuth } from "firebase/auth";
import { onAuthStateChanged } from "firebase/auth";
import { useAuthStatus } from "@src/auth/useAuthStatus";

export const fetchFeatureFlags = async () => {
  try {
    const response = await propelyApi().get("/featureFlags");
    const parsedResponse = TApiFeatureFlagsResponse.parse(response.data);
    return parsedResponse;
  } catch (error) {
    console.error("Error fetching feature flags", error);
    return null;
  }
};

export type FeatureFlagIDEnum =
  | "aiImportMode"
  | "fileContentSearch"
  | "forms"
  | "newWebAppNavbar"
  | "newWebAppTaskPage"
  | "webAppDarkMode"
  | "newWebAppTaskCreation";

export type FeatureFlagContextType = {
  featureFlags: IApiFeatureFlagsResponse["featureFlags"] | null;
  setFeatureFlags: Dispatch<SetStateAction<IApiFeatureFlagsResponse["featureFlags"] | null>>;
};

export const FeatureFlagContext = createContext<FeatureFlagContextType>({
  featureFlags: null,
  setFeatureFlags: () => {},
});

export default function FeatureFlagContextWrapper({
  children,
  setFeatureFlagsLoaded,
}: {
  children: ReactNode;
  setFeatureFlagsLoaded: (featureFlagsLoaded: boolean) => void;
}) {
  const [featureFlags, setFeatureFlags] = useState<IApiFeatureFlagsResponse["featureFlags"] | null>(null);
  const [originalFeatureFlags, setOriginalFeatureFlags] = useState<IApiFeatureFlagsResponse["featureFlags"] | null>(null);
  const { isSignedIn, isExternal, authCheckComplete } = useAuthStatus();

  //Initial load of feature flags set application state
  useEffect(() => {
    async function loadFeatureFlags() {
      try {
        if (!authCheckComplete) return;
        if (!isSignedIn || isExternal) {
          setFeatureFlagsLoaded(true);
          return;
        }
        const flags = await fetchFeatureFlags();
        if (flags) {
          setFeatureFlags(flags.featureFlags);
          setOriginalFeatureFlags(flags.featureFlags);
        }
        setFeatureFlagsLoaded(true);
      } catch (error) {
        console.error("Error fetching feature flags", error);
        setFeatureFlagsLoaded(true); //Set to true wether or not the fetch was successful, for example if the user is not logged in
      }
    }
    loadFeatureFlags();
  }, [authCheckComplete, isSignedIn, isExternal]);

  //Update feature flags in db when they change in the app
  useEffect(() => {
    if (!featureFlags || !originalFeatureFlags) return;
    let featureFlagToUpdate: IApiFeatureFlagsResponse["featureFlags"][number] | null = null;
    for (const flag of featureFlags) {
      if (flag.isEnabled !== originalFeatureFlags?.find((f) => f.id === flag.id)?.isEnabled) {
        featureFlagToUpdate = flag;
        break;
      }
    }
    if (featureFlagToUpdate) {
      asyncUpdate({ featureFlagToUpdate });
    }
    async function asyncUpdate({ featureFlagToUpdate }: { featureFlagToUpdate: IApiFeatureFlagsResponse["featureFlags"][number] }) {
      await propelyApi().patch(`/featureFlags/${featureFlagToUpdate.id}`, { isEnabled: featureFlagToUpdate.isEnabled });
      setOriginalFeatureFlags(featureFlags);
    }
  }, [featureFlags]);

  return <FeatureFlagContext.Provider value={{ featureFlags, setFeatureFlags }}>{children}</FeatureFlagContext.Provider>;
}

//Helper functions for the feature flags.

//If the whole feature flag is needed, use this instead: const { featureFlags } = useContext(FeatureFlagContext);
export function useFeatureFlag(flagID: string) {
  const { featureFlags } = useContext(FeatureFlagContext);
  return featureFlags?.find((flag) => flag.id === flagID)?.isEnabled;
}

//A function inside a hook is needed to comply with rules of hooks
export function useUpdateFeatureFlag() {
  const { setFeatureFlags } = useContext(FeatureFlagContext);

  return function updateFeatureFlag({ flagID, isEnabled, toggle }: { flagID: string; isEnabled?: boolean; toggle?: boolean }) {
    if (isEnabled === undefined && toggle === undefined) {
      console.error("No value provided for updateFeatureFlag");
      return;
    }

    setFeatureFlags((prev) => {
      return prev?.map((item) => (item.id === flagID ? { ...item, isEnabled: isEnabled ?? !item.isEnabled } : item)) ?? null;
    });
  };
}
